import { rem } from 'polished'
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import TypingAnimation from 'components/Common/TypingAnimation'
import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import ExperiencesSearchDateSelectDropdown from 'components/Search/Experiences/ExperiencesSearchDateSelectDropdown'
import FareRadio from 'components/Flights/FlightsSearch/SearchWidget/FareDropdown/FareRadio'
import TransferTravellerSelect, { DEFAULT_TRANSFER_OCCUPANTS } from './TransferTravellerSelect'
import useExperienceSearchLabels from 'components/Experiences/hooks/useExperienceSearchLabels'
import useExperienceRecentSearches from 'components/Experiences/hooks/useExperienceRecentSearches'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import type { FlightDropdownOption } from 'components/Flights/types'
import { SearchMenuStates } from 'components/Search/type'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { searchPaneInteractEventName } from 'components/Search/utils'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import {
  EXPERIENCE_TYPEAHEAD_SEARCH_TYPES,
  TRANSFER_SEARCH_VERTICAL,
} from 'constants/experience'
import { useExperienceBrandDestinationNames } from 'hooks/useExperienceBrandDestinations'
import { FlightsFareTypes } from 'constants/flight'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`

const ControlsContainer = styled.div`
  position: relative;
  display: grid;
  gap: ${rem(12)};
  grid-template:
    "location-input"
    "date-input"
    "actions" auto / 1fr;

  ${mediaQueryUp.tablet} {
    grid-template: "location-input date-input actions" auto / 1fr minmax(${rem(230)}, ${rem(304)}) ${rem(112)};
  }
`

const LocationInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: location-input;
  position: relative;
`

const AnimatedPlaceholderOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  text-align: start;
  padding: ${rem(4)} ${rem(16)};
  background: ${(props) => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.XS};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(2)};
`

const DateInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: date-input;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: actions;
`

export interface TransfersSearchExtras {
  fareType: FlightsFareTypes
  occupants: App.Occupants
}

interface Props {
  onSearchButtonClick?: (
    e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    searchItem?: App.SearchItem,
    extras?: TransfersSearchExtras,
  ) => void
  disableSearch?: boolean
  onSearchPhraseChange?: (searchPhrase: string) => void
}

function TransfersSearchInputs(props: Props) {
  const {
    onSearchButtonClick,
    disableSearch = false,
    onSearchPhraseChange,
  } = props

  const { locationLabel, locationPlaceholder, searchLabel, searchTransfersInLabel } = useExperienceSearchLabels()
  const intl = useIntl()
  const { fareTypes } = useFlightFareLabels()
  const {
    activeMenu,
    checkinDate,
    checkoutDate,
    popularDestinations,
    disableRecentSearches,
    searchItem,
  } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const recentSearches = useExperienceRecentSearches(TRANSFER_SEARCH_VERTICAL)
  const [inputFocused, setInputFocused] = useState(false)
  const [fareType, setFareType] = useState<FlightDropdownOption>(() => ({
    value: FlightsFareTypes.RETURN,
    label: '',
    disabled: false,
  }))
  const [occupants, setOccupants] = useState<App.Occupants>(DEFAULT_TRANSFER_OCCUPANTS)

  const fareOptions = useMemo(
    () => fareTypes
      .filter(option => option.value !== FlightsFareTypes.MULTI_CITY)
      .map(option => ({
        ...option,
        disabled: false,
        comingSoon: false,
      })),
    [fareTypes],
  )

  useEffect(() => {
    const matchingOption = fareOptions.find(option => option.value === fareType.value) ?? fareOptions[0]
    if (matchingOption && matchingOption.label !== fareType.label) {
      setFareType(matchingOption)
    }
  }, [fareOptions, fareType.label, fareType.value])

  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    const handleFocus = () => setInputFocused(true)

    const handleBlur = () => {
      setTimeout(() => {
        const { activeElement } = document
        if (controlsContainerRef.current?.contains(activeElement)) return
        if (activeElement?.closest('[data-testid="place-select"]')) return
        setInputFocused(false)
      }, 0)
    }

    input.addEventListener('focus', handleFocus)
    input.addEventListener('blur', handleBlur)
    return () => {
      input.removeEventListener('focus', handleFocus)
      input.removeEventListener('blur', handleBlur)
    }
  }, [])

  const recentSearchItems = useMemo(() => {
    return recentSearches.map(recentSearch => ({
      searchItem: recentSearch.searchItem,
      dates: recentSearch.dates,
      categories: recentSearch.categories,
      vertical: recentSearch.vertical,
      recentSearchId: recentSearch.recentSearchId,
      dateAdded: recentSearch.dateAdded,
      rooms: [],
    }))
  }, [recentSearches])

  const destinationNames = useExperienceBrandDestinationNames()

  const showOverlay = !searchItem && !inputFocused
  const isReturnTrip = fareType.value === FlightsFareTypes.RETURN
  const hasRequiredDates = isReturnTrip
    ? Boolean(checkinDate?.isValid() && checkoutDate?.isValid())
    : Boolean(checkinDate?.isValid())

  const searchExtras = useMemo((): TransfersSearchExtras => ({
    fareType: fareType.value as FlightsFareTypes,
    occupants,
  }), [fareType.value, occupants])

  const setActiveMenu = useCallback((menu: SearchMenuStates) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu })
  }, [globalSearchDispatch])

  const closeMenu = useCallback(() => {
    setActiveMenu(SearchMenuStates.Closed)
  }, [setActiveMenu])

  const openDatesMenu = useCallback(() => {
    document.dispatchEvent(new CustomEvent(searchPaneInteractEventName))
    setActiveMenu(SearchMenuStates.Dates)
  }, [setActiveMenu])

  const handleApply = useCallback(() => {
    if (!hasRequiredDates) {
      openDatesMenu()
      return
    }
    onSearchButtonClick?.(undefined, undefined, searchExtras)
    closeMenu()
  }, [closeMenu, hasRequiredDates, onSearchButtonClick, openDatesMenu, searchExtras])

  const handleLocationSelect = useCallback((nextSearchItem?: App.SearchItem) => {
    if (!nextSearchItem) return

    if (!hasRequiredDates) {
      openDatesMenu()
      return
    }

    onSearchButtonClick?.(undefined, nextSearchItem, searchExtras)
  }, [hasRequiredDates, onSearchButtonClick, openDatesMenu, searchExtras])

  const onToggleMenu = useCallback((nextFilter?: SearchMenuStates) => {
    if (!nextFilter || activeMenu === nextFilter) {
      closeMenu()
    } else {
      setActiveMenu(nextFilter)
    }
  }, [activeMenu, closeMenu, setActiveMenu])

  const openLocationMenu = useCallback((e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation()
    document.dispatchEvent(new CustomEvent(searchPaneInteractEventName))
    onToggleMenu(SearchMenuStates.Locations)
  }, [onToggleMenu])

  const openDatePickerMenu = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    openDatesMenu()
  }, [openDatesMenu])

  const handleFareTypeChange = useCallback((option: FlightDropdownOption) => {
    setFareType(option)
    if (option.value === FlightsFareTypes.ONE_WAY) {
      globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
    }
  }, [globalSearchDispatch])

  const handleSearchClick = useCallback((e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (!hasRequiredDates) {
      e?.preventDefault()
      openDatesMenu()
      return
    }
    onSearchButtonClick?.(e, undefined, searchExtras)
  }, [hasRequiredDates, onSearchButtonClick, openDatesMenu, searchExtras])

  const inputRef = useRef<HTMLInputElement>(null)
  const controlsContainerRef = useRef<HTMLDivElement>(null)
  const departingTriggerRef = useRef<HTMLButtonElement>(null)
  const returningTriggerRef = useRef<HTMLButtonElement>(null)

  const departureLabel = intl.formatMessage({
    id: 'tRtcXdI2JP2vTKashXWI',
    defaultMessage: 'Departure',
    description: 'Form field label for the departure date on the transfers search page',
  })
  const arrivalLabel = intl.formatMessage({
    id: 'ZKes6XNu2b3UW1bntMwD',
    defaultMessage: 'Arrival',
    description: 'Form field label for the arrival date on the transfers search return trip',
  })
  const selectDatePlaceholder = intl.formatMessage({
    id: 'WgFH00uyPfLjuOWs59-L',
    defaultMessage: 'Select date',
    description: 'Placeholder text for date selection field in flight search',
  })

  const formattedCheckInDate = checkinDate?.isValid()
    ? intlDateFormatter(checkinDate.toDate(), INTL_DMY_CASUAL_FORMAT, intl.locale)
    : undefined
  const formattedCheckOutDate = checkoutDate?.isValid()
    ? intlDateFormatter(checkoutDate.toDate(), INTL_DMY_CASUAL_FORMAT, intl.locale)
    : undefined

  return (
    <Root>
      <Group direction="horizontal" gap={32} wrap="wrap" verticalAlign="center">
        <FareRadio
          options={fareOptions}
          selectedOption={fareType}
          onOptionChange={handleFareTypeChange}
        />
        <TransferTravellerSelect
          occupants={occupants}
          onChange={setOccupants}
        />
      </Group>

      <ControlsContainer ref={controlsContainerRef}>
        <LocationInputContainer>
          {showOverlay && (
            <AnimatedPlaceholderOverlay aria-hidden="true">
              <BodyText variant="medium" weight="bold" colour="normal-contrast">{locationLabel}</BodyText>
              <BodyText variant="medium" colour="light-foreground-alt" as="span">
                {searchTransfersInLabel}&nbsp;
                <BodyText as="span" colour="normal-contrast" variant="medium" weight="bold">
                  <TypingAnimation textList={destinationNames} />
                </BodyText>
              </BodyText>
            </AnimatedPlaceholderOverlay>
          )}
          <SearchLocationInput
            data-testid="transfers-search-location-input"
            label={locationLabel}
            placeholder={locationPlaceholder}
            placeTypes={EXPERIENCE_TYPEAHEAD_SEARCH_TYPES}
            variant="dropdown"
            onLocationToggle={openLocationMenu}
            searchVertical="experience"
            inputRef={inputRef}
            controlsContainerRef={controlsContainerRef}
            onSearchPhraseChange={onSearchPhraseChange}
            placeholderItems={popularDestinations}
            recentSearchItems={disableRecentSearches ? undefined : recentSearchItems}
            onChange={handleLocationSelect}
            searchPath="/search/experiences"
            searchMapPath="/search/experiences/map"
          />
        </LocationInputContainer>
        <DateInputContainer>
          <SearchFormFieldGroup>
            <SearchFormField
              ref={departingTriggerRef}
              label={arrivalLabel}
              placeholder={selectDatePlaceholder}
              value={formattedCheckInDate}
              onClick={openDatePickerMenu}
            />
            {isReturnTrip && (
              <SearchFormField
                ref={returningTriggerRef}
                label={departureLabel}
                placeholder={selectDatePlaceholder}
                value={formattedCheckOutDate}
                onClick={openDatePickerMenu}
              />
            )}
          </SearchFormFieldGroup>
          <ExperiencesSearchDateSelectDropdown
            anchorRef={controlsContainerRef}
            triggerRef={departingTriggerRef}
            open={activeMenu === SearchMenuStates.Dates}
            onApply={handleApply}
            onDecline={closeMenu}
            requireCheckout={isReturnTrip}
            allowAnytime={false}
          />
        </DateInputContainer>
        <ActionsContainer>
          <TextButton
            kind="primary"
            type="submit"
            size="large"
            onClick={handleSearchClick}
            disabled={disableSearch || !hasRequiredDates}
          >
            {searchLabel}
          </TextButton>
        </ActionsContainer>
      </ControlsContainer>
    </Root>
  )
}

export default memo(TransfersSearchInputs)
